import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { todayISO } from "../../lib/format";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import ProofUploader from "./ProofUploader";

/**
 * K234 / BUSINESS-113 sisi STAF ("113 setuju.", Owner 31 Okt 2026) — isian transfer manual yang disederhanakan,
 * dipakai tanda jadi (layar PD), uang muka (DP) dan pelunasan (layar FA).
 *
 * Pertanyaan Owner yang melahirkannya: "mana kita tahu nomor rek dan bank pengirim sebelum terealisasi pembayaran
 * tanda jadinya ?" — isian ini dicatat SESUDAH customer mentransfer, datanya dibaca dari bukti. Jadi:
 *   * Rekening penerima = PILIHAN rekening resmi ETAMS cabang pesanan (master `bank_accounts`), bukan diketik;
 *   * Tanggal transfer otomatis hari ini (bisa diubah);
 *   * Bank pengirim & no. referensi OPSIONAL;
 *   * Yang WAJIB hanya bukti (tombol kirim di induknya mati sampai bukti terunggah — `isianTransferLengkap`).
 * Kepala Cabang tetap mencocokkan dengan nominal di mutasi rekening (K221/K227).
 */
export function isianTransferAwal() {
  return { file_url: "", bank_from: "", bank_to: "", transfer_date: todayISO(), reference_number: "", notes: "" };
}

export function isianTransferLengkap(isian) {
  return !!isian?.file_url;
}

/** Muatan `proof` untuk `POST /payments/submit` — isian kosong dikirim null, bukan teks kosong. */
export function muatanBukti(isian) {
  const kosongJadiNull = (v) => ((v || "").trim() ? v.trim() : null);
  return {
    file_url: isian.file_url || null,
    bank_from: kosongJadiNull(isian.bank_from),
    bank_to: kosongJadiNull(isian.bank_to),
    transfer_date: isian.transfer_date || null,
    reference_number: kosongJadiNull(isian.reference_number),
    notes: kosongJadiNull(isian.notes),
  };
}

export default function IsianTransferManual({ storeId, isian, setIsian, testIdPrefix = "transfer" }) {
  const [rekening, setRekening] = useState(null);   // null = memuat / tidak terbaca
  useEffect(() => {
    if (!storeId) { setRekening([]); return; }
    let hidup = true;
    api.get(`/payments/rekening-resmi/${storeId}`)
      .then((r) => {
        if (!hidup) return;
        const items = r.data?.items || [];
        setRekening(items);
        // Rekening utama terpilih otomatis (bisa diganti) — staf tidak perlu mengetik apa pun di sini.
        if (items.length) setIsian((s) => (s.bank_to ? s : { ...s, bank_to: items[0].label }));
      })
      .catch(() => { if (hidup) setRekening([]); });
    return () => { hidup = false; };
  }, [storeId, setIsian]);

  const ubah = (k) => (e) => setIsian((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-isian`}>
      <div>
        <Label>Bukti transfer <span className="text-destructive">(wajib)</span></Label>
        <ProofUploader testIdPrefix={`${testIdPrefix}-upload`} onUploaded={(r) => setIsian((s) => ({ ...s, file_url: r.file_url }))} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <Label>Rekening penerima (rekening resmi cabang)</Label>
          {rekening && rekening.length > 0 ? (
            <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={isian.bank_to} onChange={ubah("bank_to")} data-testid={`${testIdPrefix}-bank-to`}>
              {rekening.map((r) => <option key={r.id} value={r.label}>{r.label}{r.utama ? " (utama)" : ""}</option>)}
            </select>
          ) : (
            <div className="text-xs text-muted-foreground py-2" data-testid={`${testIdPrefix}-rekening-kosong`}>
              {rekening === null ? "Memuat rekening…" : "Rekening resmi cabang ini belum ada di data rekening. Hubungi Owner — bukti tetap bisa dikirim."}
            </div>
          )}
        </div>
        <div>
          <Label>Tanggal transfer</Label>
          <Input type="date" value={isian.transfer_date} onChange={ubah("transfer_date")} data-testid={`${testIdPrefix}-tanggal`} />
        </div>
        <div>
          <Label>Bank pengirim (opsional)</Label>
          <Input value={isian.bank_from} onChange={ubah("bank_from")} placeholder="Terbaca dari bukti" data-testid={`${testIdPrefix}-bank-from`} />
        </div>
        <div>
          <Label>No. referensi (opsional)</Label>
          <Input value={isian.reference_number} onChange={ubah("reference_number")} placeholder="Terbaca dari bukti" data-testid={`${testIdPrefix}-ref`} />
        </div>
        <div className="sm:col-span-2">
          <Label>Catatan (opsional)</Label>
          <Input value={isian.notes} onChange={ubah("notes")} data-testid={`${testIdPrefix}-catatan`} />
        </div>
      </div>
    </div>
  );
}
